import React, { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";

export interface ResumeFileData {
  name: string;
  type: string;
  size: number;
  binary: ArrayBuffer;
}

interface ResumePickerProps {
  file: ResumeFileData | null;
  onChange: (file: ResumeFileData | null) => void;
}

const ResumePicker: React.FC<ResumePickerProps> = ({ file, onChange }) => {
  const [dragOver, setDragOver] = useState(false);

  const onDrop = useCallback(
    (accepted: File[]) => {
      setDragOver(false);
      if (!accepted.length) return;
      const f = accepted[0];
      const reader = new FileReader();
      reader.onload = () => {
        onChange({
          name: f.name,
          type: f.type,
          size: f.size,
          binary: reader.result as ArrayBuffer,
        });
      };
      reader.readAsArrayBuffer(f);
    },
    [onChange]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "application/pdf": [".pdf"] },
    multiple: false,
    onDragEnter: () => setDragOver(true),
    onDragLeave: () => setDragOver(false),
  });

  const formatSize = (bytes: number) =>
    bytes < 1024 * 1024
      ? `${(bytes / 1024).toFixed(1)} KB`
      : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

  if (file) {
    return (
      <div className="interview-hub-resume-picker-selected">
        <div className="interview-hub-resume-picker-file-icon">📄</div>
        <div className="interview-hub-resume-picker-file-info">
          <span className="interview-hub-resume-picker-file-name">{file.name}</span>
          <span className="interview-hub-resume-picker-file-size">{formatSize(file.size)}</span>
        </div>
        <button
          className="interview-hub-resume-picker-remove"
          onClick={() => onChange(null)}
          title="Remove file"
        >
          ✕
        </button>
      </div>
    );
  }

  return (
    <div
      {...getRootProps()}
      className={`interview-hub-resume-picker-dropzone ${isDragActive || dragOver ? "interview-hub-resume-picker-drag" : ""}`}
    >
      <input {...getInputProps()} />
      <span className="interview-hub-resume-picker-icon">⬆️</span>
      <p className="interview-hub-resume-picker-label">
        Drop your PDF here or <span className="interview-hub-resume-picker-browse">browse</span>
      </p>
      <p className="interview-hub-resume-picker-hint">PDF only · Max 10 MB</p>
    </div>
  );
};

export default ResumePicker;
